
import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { ActionCardProps, ActionCardField } from '@/types/action-card';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, SendHorizontal } from 'lucide-react';
import { toast } from "sonner";

export const InputCard = ({ data, className }: ActionCardProps) => {
  const [formState, setFormState] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const updateField = (id: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      [id]: value
    }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (data.fields) {
      const requiredFields = data.fields.filter(field => field.required);
      const missingFields = requiredFields.filter(field => 
        !formState[field.id] && formState[field.id] !== false
      );
      
      if (missingFields.length > 0) {
        toast.error(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
        return;
      }
    }
    
    setIsSubmitting(true);
    
    // In a real application, this would send the data to the backend
    setTimeout(() => {
      toast.success("Input submitted successfully");
      setIsSubmitting(false);
      setSubmitted(true);
      
      // Here you would dispatch an action to handle the form submission
      console.log('Form submitted with data:', formState);
    }, 1000);
  };

  const renderField = (field: ActionCardField) => {
    switch (field.type) {
      case 'text':
        return (
          <div className="space-y-2" key={field.id}>
            <Label htmlFor={field.id} className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <Input
              id={field.id}
              value={formState[field.id] || ''}
              onChange={(e) => updateField(field.id, e.target.value)}
              placeholder={field.placeholder}
              className="bg-white/10 dark:bg-gray-800/30 border-white/10 dark:border-gray-800/30"
            />
          </div>
        );
      case 'textarea':
        return (
          <div className="space-y-2" key={field.id}>
            <Label htmlFor={field.id} className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <Textarea
              id={field.id}
              value={formState[field.id] || ''}
              onChange={(e) => updateField(field.id, e.target.value)}
              placeholder={field.placeholder}
              className="bg-white/10 dark:bg-gray-800/30 border-white/10 dark:border-gray-800/30"
            />
          </div>
        );
      case 'select':
        return (
          <div className="space-y-2" key={field.id}>
            <Label htmlFor={field.id} className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <Select
              value={formState[field.id] || ''}
              onValueChange={(value) => updateField(field.id, value)}
            >
              <SelectTrigger className="bg-white/10 dark:bg-gray-800/30 border-white/10 dark:border-gray-800/30">
                <SelectValue placeholder={field.placeholder || "Select option"} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2" key={field.id}>
            <Checkbox
              id={field.id}
              checked={!!formState[field.id]}
              onCheckedChange={(checked) => updateField(field.id, checked)}
            />
            <Label htmlFor={field.id} className="text-sm">
              {field.label}
            </Label>
          </div>
        );
      case 'radio':
        return (
          <div className="space-y-2" key={field.id}>
            <Label className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <RadioGroup
              value={formState[field.id] || ''}
              onValueChange={(value) => updateField(field.id, value)}
            >
              {field.options?.map(option => (
                <div className="flex items-center space-x-2" key={option.value}>
                  <RadioGroupItem value={option.value} id={`${field.id}-${option.value}`} />
                  <Label htmlFor={`${field.id}-${option.value}`} className="text-sm">
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
      className={cn(
        "relative",
        "shadow-lg hover:shadow-xl transition-all duration-300",
        className
      )}
    >
      <Card className={cn(
        "backdrop-blur-sm bg-white/10 dark:bg-gray-900/20",
        "border border-white/20 dark:border-gray-800/30",
        "bg-gradient-to-br from-primary/10 to-primary/5",
        "p-4"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10 dark:border-gray-800/50">
          <div className="flex items-center gap-3">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 dark:bg-gray-800/30"
            >
              <MessageSquare className="h-4 w-4 text-primary" />
            </motion.div>
            <div className="space-y-0.5">
              <h3 className="text-lg font-semibold tracking-tight">{data.title}</h3>
              <p className="text-sm text-muted-foreground">{data.summary}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence>
          {submitted ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-6 text-center"
            >
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-3">
                <SendHorizontal className="h-6 w-6 text-primary" />
              </div>
              <h4 className="text-lg font-medium mb-2">Thank You</h4>
              <p className="text-muted-foreground">Your input has been received.</p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSubmit}
              className="py-4 space-y-4"
            >
              {data.fields?.map(field => renderField(field))}
              
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className={cn(
                  "w-full",
                  isSubmitting && "opacity-70"
                )}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Timestamp */}
        <div className="pt-2 text-[10px] text-muted-foreground">
          {data.timestamp}
        </div>
      </Card>
    </motion.div>
  );
};
